// /src/pages/vehicles/InnovaCrystaBangalore.js
import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { Car, Users, CheckCircle, MapPin, Phone, Shield, Star, ArrowLeft } from 'lucide-react';
import Image31 from "../../Assets/innovacrysta/2021-Toyota-Innova-Crysta-Facelift-Side.jpg";
import Image32 from "../../Assets/innovacrysta/unrivaled-connectivity-banner1600x1000-new.jpg";
import Image33 from "../../Assets/innovacrysta/front-left-side-47 (2).png";

const InnovaCrystaBangalore = () => {
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = React.useState(0);
  const [showBookingForm, setShowBookingForm] = React.useState(false);
  
  const images = [Image31, Image32, Image33];
  
  // UNIQUE Schema for Innova Crysta
  const vehicleSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "Toyota Innova Crysta Booking in Bangalore - Nava Cabs",
    "description": "Book Toyota Innova Crysta for airport transfers, corporate travel & family trips in Bangalore. 7-seater, diesel, premium comfort.",
    "image": images,
    "brand": { 
      "@type": "Brand", 
      "name": "Toyota",
      "logo": "https://www.navacabs.com/logo.png"
    },
    "model": "Innova Crysta",
    "vehicleEngine": {
      "@type": "EngineSpecification",
      "engineType": "Diesel",
      "engineDisplacement": "2.4L/2.8L"
    },
    "vehicleTransmission": "Manual/Automatic",
    "seatingCapacity": "7",
    "fuelType": "Diesel",
    "mileageFromOdometer": {
      "@type": "QuantitativeValue",
      "value": "12-15",
      "unitCode": "KMPL"
    },
    "offers": {
      "@type": "Offer",
      "priceCurrency": "INR",
      "price": "20",
      "priceValidUntil": "2025-12-31",
      "availability": "https://schema.org/InStock",
      "areaServed": {
        "@type": "City",
        "name": "Bangalore",
        "sameAs": "https://en.wikipedia.org/wiki/Bangalore"
      }
    }
  };

  const features = [
    'Powerful 2.4L/2.8L Diesel Engine',
    'Spacious 7-Seater Cabin',
    'Captain Seats (Optional)',
    'Dual Zone AC with Rear Vents',
    'Touchscreen Infotainment System',
    'Rear Parking Camera with Sensors',
    'Power Windows & Central Locking',
    'Comfort Suspension for Long Journeys',
    'Premium Leather Upholstery',
    'ABS with EBD for Safety'
  ];

  const specifications = {
    'Vehicle Type': 'Toyota Innova Crysta',
    'Seating Capacity': '7 Passengers',
    'Engine': '2.4L/2.8L Diesel',
    'Transmission': 'Manual/Automatic Available',
    'Luggage Capacity': '6-7 Large Bags',
    'AC': 'Dual Zone Climate Control',
    'Fuel Type': 'Diesel',
    'Fuel Efficiency': '12-15 km/l',
    'Airbags': 'Dual Front Airbags',
    'Entertainment': '8-inch Touchscreen with Navigation'
  };

  const nextImage = () => setCurrentImageIndex((prev) => (prev + 1) % images.length);
  const prevImage = () => setCurrentImageIndex((prev) => prev === 0 ? images.length - 1 : prev - 1);

  if (showBookingForm) {
    return <BookingForm vehicle={{
      name: 'Toyota Innova Crysta',
      seating: 7,
      priceRange: '₹18-20/km'
    }} onBack={() => setShowBookingForm(false)} />;
  }

  return (
    <>
      <Helmet>
        {/* PRIMARY SEO TAGS */}
        <title>Toyota Innova Crysta Booking Bangalore | 7-Seater @ ₹20/km - Nava Cabs</title>
        <meta 
          name="description" 
          content="✅ Book Toyota Innova Crysta in Bangalore for airport transfers, corporate trips & family travel. 7-seater diesel MUV with professional drivers. Call +91 99647 58999."
        />
        <meta 
          name="keywords" 
          content="innova booking bangalore, toyota innova crysta rental, innova car rental bangalore, bangalore airport taxi innova, innova crysta 7 seater, book innova in bangalore, innova outstation trips"
        />
        
        {/* OPEN GRAPH */}
        <meta property="og:title" content="Toyota Innova Crysta Booking in Bangalore - Nava Cabs" />
        <meta property="og:description" content="Premium Toyota Innova Crysta rental service in Bangalore for all your travel needs." />
        <meta property="og:image" content={images[0]} />
        <meta property="og:url" content="https://www.urbanianavacabs.com/innova-booking-bangalore" />
        <meta property="og:type" content="website" />
        
        {/* TWITTER */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Toyota Innova Crysta Booking Bangalore" />
        <meta name="twitter:description" content="Book Toyota Innova Crysta in Bangalore - Premium 7-seater MUV rental service" />
        
        {/* CANONICAL */}
        <link rel="canonical" href="https://www.urbanianavacabs.com/innova-booking-bangalore" />
        
        {/* SCHEMA MARKUP */}
        <script type="application/ld+json">
          {JSON.stringify(vehicleSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Back Button */}
        <div className="bg-white shadow-sm">
          <div className="container mx-auto px-4 py-3">
            <button 
              onClick={() => navigate(-1)}
              className="flex items-center text-gray-600 hover:text-blue-600 transition-colors text-sm"
            >
              ← Back
            </button>
          </div>
        </div>

        {/* Hero Section with H1 */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-12">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-3xl md:text-4xl font-bold mb-3">
              Toyota Innova Crysta Booking in Bangalore
            </h1>
            <p className="text-lg opacity-90 max-w-2xl mx-auto">
              Premium 7-seater Toyota Innova Crysta rental service for airport transfers, 
              corporate travel, family trips, and outstation journeys from Bangalore.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
                         <div className="bg-white/20 px-3 py-1.5 rounded-full flex items-center text-sm">
                           <Users className="w-4 h-4 mr-1.5" /> 7 Seater
                         </div>
                         <div className="bg-white/20 px-3 py-1.5 rounded-full flex items-center text-sm">
                           <Car className="w-4 h-4 mr-1.5" /> Diesel
                         </div>
                         <div className="bg-white/20 px-3 py-1.5 rounded-full flex items-center text-sm">
                           ₹18-20/km
                         </div>
                       </div>
          </div>
        </div>

        <div className="container mx-auto px-4 py-12">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Image Gallery */}
              <div className="bg-white rounded-xl shadow-lg p-4">
                <div className="relative rounded-lg overflow-hidden">
                  <img 
                    src={images[currentImageIndex]} 
                    alt="Toyota Innova Crysta car interior and exterior views - Nava Cabs Bangalore"
                    className="w-full h-64 md:h-80 object-cover"
                  />
                  <button 
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 p-2 rounded-full"
                  >
                    ←
                  </button>
                  <button 
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 p-2 rounded-full"
                  >
                    →
                  </button>
                </div>

                <div className="lg:hidden mt-6">
                                  <div className="text-center mb-4">
                                    <div className="text-3xl font-bold text-blue-600 mb-1">₹18-20/km</div>
                                    <div className="text-gray-600 text-sm">Starting Price</div>
                                  </div>
                                  <button 
                                    onClick={() => setShowBookingForm(true)}
                                    className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-4 rounded-xl font-bold text-lg hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg hover:shadow-xl"
                                  >
                                    Book Innova Crysta Now
                                  </button>
                                  
                                  {/* Quick Contact on Mobile */}
                                  <div className="mt-4 space-y-3">
                                    <a 
                                      href="tel:+919964758999" 
                                      className="flex items-center justify-center gap-3 bg-green-50 text-green-800 p-3 rounded-xl hover:bg-green-100 transition-colors"
                                    >
                                      <Phone className="w-5 h-5" />
                                      <div className="text-left">
                                        <div className="font-bold">+91 99647 58999</div>
                                        <div className="text-xs">Call for Instant Booking</div>
                                      </div>
                                    </a>
                                    <a 
                                      href="https://wa.me/919964758999" 
                                      className="flex items-center justify-center gap-3 bg-green-600 text-white p-3 rounded-xl hover:bg-green-700 transition-colors"
                                    >
                                      <div className="font-bold">WhatsApp Booking</div>
                                    </a>
                                  </div>
                                </div>
              </div>

              {/* SEO Content Section */}
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  Toyota Innova Crysta Rental Service in Bangalore
                </h2>
                
                <div className="prose max-w-none text-gray-700 space-y-4">
                  <p>
                    <strong>Nava Cabs offers premium Toyota Innova Crysta booking services in Bangalore</strong> for all your travel needs. 
                    Whether you need a reliable vehicle for <strong>airport pickups from Kempegowda International Airport (BLR)</strong>, 
                    <strong> corporate travel</strong> between business districts, <strong>family trips</strong> to nearby destinations, 
                    or <strong>wedding functions</strong>, our well-maintained Innova Crysta fleet is ready to serve you.
                  </p>
                  
                  <p>
                    Our Toyota Innova Crysta vehicles are perfect for <strong>outstation trips from Bangalore to Mysore, Coorg, Ooty, Tirupati, 
                    and Goa</strong>. With spacious seating for 7 passengers and ample luggage space, you can travel comfortably 
                    with your entire family or group of colleagues.
                  </p>
                  
                  <h3 className="text-2xl font-bold mt-8 mb-4">Service Areas in Bangalore</h3>
                  <p>
                    We provide <strong>Innova Crysta booking services across all Bangalore areas</strong> including Koramangala, 
                    Indiranagar, Whitefield, Marathahalli, Electronic City, HSR Layout, JP Nagar, MG Road, Jayanagar, 
                    Bellandur, Sarjapur Road, and Hebbal. Same-day booking available for urgent requirements.
                  </p>
                  
                  <h3 className="text-2xl font-bold mt-8 mb-4">Why Choose Our Innova Crysta Service?</h3>
                  <ul className="grid md:grid-cols-2 gap-3">
                    <li className="flex items-center"><CheckCircle className="w-5 h-5 text-green-500 mr-2" /> 24/7 Availability</li>
                    <li className="flex items-center"><CheckCircle className="w-5 h-5 text-green-500 mr-2" /> Professional Drivers</li>
                    <li className="flex items-center"><CheckCircle className="w-5 h-5 text-green-500 mr-2" /> GPS Equipped Vehicles</li>
                    <li className="flex items-center"><CheckCircle className="w-5 h-5 text-green-500 mr-2" /> Fixed Pricing - No Hidden Charges</li>
                    <li className="flex items-center"><CheckCircle className="w-5 h-5 text-green-500 mr-2" /> Well-maintained Fleet</li>
                    <li className="flex items-center"><CheckCircle className="w-5 h-5 text-green-500 mr-2" /> Insurance Covered</li>
                  </ul>
                </div>
              </div>

              {/* Features */}
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Innova Crysta Features</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  {features.map((feature, index) => (
                    <div key={index} className="flex items-center p-3 bg-blue-50 rounded-lg">
                      <CheckCircle className="w-5 h-5 text-blue-600 mr-3" />
                      <span className="text-gray-800">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Specifications */}
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Technical Specifications</h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.entries(specifications).map(([key, value]) => (
                    <div key={key} className="bg-gray-50 p-4 rounded-lg">
                      <div className="text-sm text-gray-600 font-medium">{key}</div>
                      <div className="text-lg font-semibold text-gray-900">{value}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Sidebar - Booking & Contact */}
              <div className="hidden lg:block lg:col-span-1">
              <div className="bg-white rounded-xl shadow-lg p-6 sticky top-24">
                <div className="text-center mb-6">
                  <div className="text-4xl font-bold text-blue-600 mb-2">₹18-20/km</div>
                  <div className="text-gray-600">Starting Price</div>
                  <div className="text-sm text-gray-500 mt-2">*Price varies based on trip type & duration</div>
                </div>

                <button 
                  onClick={() => setShowBookingForm(true)}
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-4 rounded-xl font-bold text-lg mb-6 hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg hover:shadow-xl"
                >
                  Book Innova Crysta Now
                </button>

                <div className="space-y-4">
                  <a 
                    href="tel:+919964758999" 
                    className="flex items-center justify-center gap-3 bg-green-50 text-green-800 p-4 rounded-xl hover:bg-green-100 transition-colors"
                  >
                    <Phone className="w-6 h-6" />
                    <div className="text-left">
                      <div className="font-bold text-lg">+91 99647 58999</div>
                      <div className="text-sm">Call for Instant Booking</div>
                    </div>
                  </a>

                  <div className="border-t pt-6">
                    <h3 className="font-bold text-gray-900 text-xl mb-4">Quick Enquiry</h3>
                    <div className="space-y-3">
                      <a href="https://wa.me/919964758999" className="block bg-green-600 text-white p-3 rounded-lg text-center hover:bg-green-700">
                        WhatsApp: +91 99647 58999
                      </a>
                      <a href="mailto:info@navacabs.com" className="block bg-blue-600 text-white p-3 rounded-lg text-center hover:bg-blue-700">
                        Email: info@navacabs.com
                      </a>
                    </div>
                  </div>

                  <div className="border-t pt-6">
                    <h3 className="font-bold text-gray-900 text-xl mb-4">Service Highlights</h3>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <Shield className="w-5 h-5 text-green-600" />
                        <div>
                          <div className="font-semibold">Insured Vehicles</div>
                          <div className="text-sm text-gray-600">Full insurance coverage</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Star className="w-5 h-5 text-yellow-500" />
                        <div>
                          <div className="font-semibold">4.8/5 Rating</div>
                          <div className="text-sm text-gray-600">500+ happy customers</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <MapPin className="w-5 h-5 text-blue-600" />
                        <div>
                          <div className="font-semibold">All Bangalore Areas</div>
                          <div className="text-sm text-gray-600">Wide service coverage</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// Booking Form Component (Reuse from your original code)
const BookingForm = ({ vehicle, onBack }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    pickupLocation: '',
    dropLocation: '',
    pickupDate: '',
    pickupTime: '',
    dropDate: '',
    dropTime: '',
    tripType: 'oneway',
    passengers: '',
    specialRequests: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone is required';
    } else if (!/^\d{10}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Phone must be 10 digits';
    }
    if (!formData.pickupLocation.trim()) newErrors.pickupLocation = 'Pickup location is required';
    if (!formData.dropLocation.trim()) newErrors.dropLocation = 'Drop location is required';
    if (!formData.pickupDate) newErrors.pickupDate = 'Pickup date is required';
    if (!formData.pickupTime) newErrors.pickupTime = 'Pickup time is required';
    
    if (formData.tripType === 'roundtrip') {
      if (!formData.dropDate) newErrors.dropDate = 'Return date is required';
      if (!formData.dropTime) newErrors.dropTime = 'Return time is required';
    }
    
    if (!formData.passengers) {
      newErrors.passengers = 'Number of passengers is required';
    } else if (parseInt(formData.passengers) > vehicle.seating) {
      newErrors.passengers = `Maximum ${vehicle.seating} passengers allowed`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

   const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };


  const sendWhatsAppMessage = () => {
    // Format the message
    const message = `*New Booking Request - Nava Cabs*

*Customer Details:*
Name: ${formData.name}
Email: ${formData.email}
Phone: ${formData.phone}
Passengers: ${formData.passengers}

*Trip Details:*
Vehicle: ${vehicle.name} (${vehicle.seating} seater)
Pickup: ${formData.pickupLocation}
Drop: ${formData.dropLocation}
Trip Type: ${formData.tripType === 'oneway' ? 'One Way' : 'Round Trip'}

*Schedule:*
Pickup Date: ${formatDate(formData.pickupDate)}
Pickup Time: ${formData.pickupTime}
${formData.tripType === 'roundtrip' ? `Return Date: ${formatDate(formData.dropDate)}\nReturn Time: ${formData.dropTime}` : ''}

*Special Requests:* ${formData.specialRequests || 'None'}

*Estimated Price:* ${vehicle.priceRange} (Contact for exact quote)

---
*Submitted via Nava Cabs Website*`;

    // Encode the message for URL
    const encodedMessage = encodeURIComponent(message);
    
    // WhatsApp number (use your business number)
    const whatsappNumber = '919916788688';
    
    // Create WhatsApp URL
    const whatsappURL = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;
    
    // Open WhatsApp in new tab
    window.open(whatsappURL, '_blank');
  };


   const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Send to WhatsApp
      sendWhatsAppMessage();
      
      // Also send to your backend if needed
      const bookingData = {
        ...formData,
        vehicle: vehicle.name,
        vehicleType: vehicle.type,
        priceRange: vehicle.priceRange,
        submittedAt: new Date().toISOString()
      };
      
      // You can also send to your API endpoint
      console.log('Booking Data for API:', bookingData);
      
      // Show success message
      alert('Booking request submitted successfully! Opening WhatsApp to confirm details...');
      
      // Close form after 2 seconds
      setTimeout(() => {
        setIsSubmitting(false);
        onBack();
        navigate('/');
      }, 2000);
      
    } catch (error) {
      console.error('Error submitting booking:', error);
      alert('Error submitting booking. Please try again.');
      setIsSubmitting(false);
    }
  };

  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="container mx-auto px-4">
        <button 
          onClick={onBack}
          className="flex items-center text-gray-600 hover:text-blue-600 transition-colors mb-5 text-sm"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          <span className="font-medium">Back to Vehicle Details</span>
        </button>

        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-lg shadow-md p-5 md:p-6">
            <div className="mb-6">
              <h1 className="text-xl font-bold text-gray-800 mb-1">Book Your Ride</h1>
              <p className="text-gray-600 text-sm">Complete the form below to book {vehicle.name}</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Personal Information */}
              <div>
                <h2 className="text-base font-semibold text-gray-800 mb-3 flex items-center gap-1">
                  <Users className="w-4 h-4 text-blue-600" />
                  Personal Information
                </h2>
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 rounded-md border ${
                        errors.name ? 'border-red-500' : 'border-gray-300'
                      } focus:ring-1 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-sm`}
                      placeholder="Enter your full name"
                    />
                    {errors.name && <p className="text-red-500 text-xs mt-0.5">{errors.name}</p>}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 rounded-md border ${
                        errors.email ? 'border-red-500' : 'border-gray-300'
                      } focus:ring-1 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-sm`}
                      placeholder="your@email.com"
                    />
                    {errors.email && <p className="text-red-500 text-xs mt-0.5">{errors.email}</p>}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 rounded-md border ${
                        errors.phone ? 'border-red-500' : 'border-gray-300'
                      } focus:ring-1 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-sm`}
                      placeholder="10-digit mobile number"
                    />
                    {errors.phone && <p className="text-red-500 text-xs mt-0.5">{errors.phone}</p>}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Number of Passengers *
                    </label>
                    <input
                      type="number"
                      name="passengers"
                      value={formData.passengers}
                      onChange={handleChange}
                      min="1"
                      max={vehicle.seating}
                      className={`w-full px-3 py-2 rounded-md border ${
                        errors.passengers ? 'border-red-500' : 'border-gray-300'
                      } focus:ring-1 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-sm`}
                      placeholder={`Max ${vehicle.seating} passengers`}
                    />
                    {errors.passengers && <p className="text-red-500 text-xs mt-0.5">{errors.passengers}</p>}
                  </div>
                </div>
              </div>

              {/* Trip Details */}
              <div>
                <h2 className="text-base font-semibold text-gray-800 mb-3 flex items-center gap-1">
                  <MapPin className="w-4 h-4 text-blue-600" />
                  Trip Details
                </h2>
                
                <div className="mb-3">
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Trip Type *
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, tripType: 'oneway' }))}
                      className={`py-2 px-3 rounded-md border font-medium transition-all text-sm ${
                        formData.tripType === 'oneway'
                          ? 'border-blue-600 bg-blue-50 text-blue-700'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      One Way
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, tripType: 'roundtrip' }))}
                      className={`py-2 px-3 rounded-md border font-medium transition-all text-sm ${
                        formData.tripType === 'roundtrip'
                          ? 'border-blue-600 bg-blue-50 text-blue-700'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      Round Trip
                    </button>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Pickup Location *
                    </label>
                    <input
                      type="text"
                      name="pickupLocation"
                      value={formData.pickupLocation}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 rounded-md border ${
                        errors.pickupLocation ? 'border-red-500' : 'border-gray-300'
                      } focus:ring-1 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-sm`}
                      placeholder="Enter pickup location"
                    />
                    {errors.pickupLocation && <p className="text-red-500 text-xs mt-0.5">{errors.pickupLocation}</p>}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Drop Location *
                    </label>
                    <input
                      type="text"
                      name="dropLocation"
                      value={formData.dropLocation}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 rounded-md border ${
                        errors.dropLocation ? 'border-red-500' : 'border-gray-300'
                      } focus:ring-1 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-sm`}
                      placeholder="Enter drop location"
                    />
                    {errors.dropLocation && <p className="text-red-500 text-xs mt-0.5">{errors.dropLocation}</p>}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Pickup Date *
                    </label>
                    <input
                      type="date"
                      name="pickupDate"
                      value={formData.pickupDate}
                      onChange={handleChange}
                      min={today}
                      className={`w-full px-3 py-2 rounded-md border ${
                        errors.pickupDate ? 'border-red-500' : 'border-gray-300'
                      } focus:ring-1 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-sm`}
                    />
                    {errors.pickupDate && <p className="text-red-500 text-xs mt-0.5">{errors.pickupDate}</p>}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Pickup Time *
                    </label>
                    <input
                      type="time"
                      name="pickupTime"
                      value={formData.pickupTime}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 rounded-md border ${
                        errors.pickupTime ? 'border-red-500' : 'border-gray-300'
                      } focus:ring-1 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-sm`}
                    />
                    {errors.pickupTime && <p className="text-red-500 text-xs mt-0.5">{errors.pickupTime}</p>}
                  </div>

                  {formData.tripType === 'roundtrip' && (
                    <>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Return Date *
                        </label>
                        <input
                          type="date"
                          name="dropDate"
                          value={formData.dropDate}
                          onChange={handleChange}
                          min={formData.pickupDate || today}
                          className={`w-full px-3 py-2 rounded-md border ${
                            errors.dropDate ? 'border-red-500' : 'border-gray-300'
                          } focus:ring-1 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-sm`}
                        />
                        {errors.dropDate && <p className="text-red-500 text-xs mt-0.5">{errors.dropDate}</p>}
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Return Time *
                        </label>
                        <input
                          type="time"
                          name="dropTime"
                          value={formData.dropTime}
                          onChange={handleChange}
                          className={`w-full px-3 py-2 rounded-md border ${
                            errors.dropTime ? 'border-red-500' : 'border-gray-300'
                          } focus:ring-1 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-sm`}
                        />
                        {errors.dropTime && <p className="text-red-500 text-xs mt-0.5">{errors.dropTime}</p>}
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Special Requests */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Special Requests (Optional)
                </label>
                <textarea
                  name="specialRequests"
                  value={formData.specialRequests}
                  onChange={handleChange}
                  rows="3"
                  className="w-full px-3 py-2 rounded-md border border-gray-300 focus:ring-1 focus:ring-blue-500 focus:border-transparent outline-none transition-all resize-none text-sm"
                  placeholder="Any special requirements or requests..."
                ></textarea>
              </div>

              {/* Vehicle Summary */}
              <div className="bg-blue-50 rounded-md p-4 border border-blue-200">
                <h3 className="font-semibold text-gray-800 mb-2 text-sm">Selected Vehicle</h3>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-800 text-sm">{vehicle.name}</p>
                    <p className="text-xs text-gray-600">{vehicle.seating} Seater • {vehicle.type}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-600">Starting from</p>
                    <p className="text-base font-bold text-blue-600">{vehicle.priceRange}</p>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex flex-col sm:flex-row gap-3 pt-3">
                <button
                  type="button"
                  onClick={onBack}
                  className="flex-1 py-2.5 px-5 rounded-md border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-all text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 py-2.5 px-5 rounded-md bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold hover:from-blue-700 hover:to-blue-800 transition-all shadow-sm hover:shadow disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  {isSubmitting ? 'Submitting...' : 'Confirm Booking'}
                </button>
              </div>

              <p className="text-xs text-gray-500 text-center">
                By submitting this form, you agree to our terms and conditions. We'll contact you shortly to confirm your booking.
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InnovaCrystaBangalore;